import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    alpha,
    Alert,
    Box,
    Button,
    Chip,
    Container,
    Paper,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import PostAddIcon from "@mui/icons-material/PostAdd";
import { getTeacherFreeClassSubjects } from "../../../redux/sclassRelated/sclassHandle";
import { removeTeachSubject, updateTeachSubject } from "../../../redux/teacherRelated/teacherHandle";
import { dedupeSubjectsById, getSubjectTeacherLabel } from "../../../utils/dedupeSubjects";
import { fetchCurrentPeriod } from "../../../utils/academicPeriodApi";
import { getSchoolIdForUser } from "../../../utils/userContext";

const FREE_SUBJECT_EMPTY = {
    NO_SUBJECTS_IN_CLASS: "NO_SUBJECTS_IN_CLASS",
    ALL_SUBJECTS_ASSIGNED: "ALL_SUBJECTS_ASSIGNED",
    NO_SUBJECT_SLOTS_FOR_TEACHER: "NO_SUBJECT_SLOTS_FOR_TEACHER",
};

function normalizeEmptySubjectReason(response) {
    const raw = String(response || "").trim();
    if (raw === FREE_SUBJECT_EMPTY.NO_SUBJECTS_IN_CLASS) return FREE_SUBJECT_EMPTY.NO_SUBJECTS_IN_CLASS;
    if (raw === FREE_SUBJECT_EMPTY.ALL_SUBJECTS_ASSIGNED) return FREE_SUBJECT_EMPTY.ALL_SUBJECTS_ASSIGNED;
    if (raw === FREE_SUBJECT_EMPTY.NO_SUBJECT_SLOTS_FOR_TEACHER) {
        return FREE_SUBJECT_EMPTY.NO_SUBJECT_SLOTS_FOR_TEACHER;
    }
    if (/no subjects found/i.test(raw)) return FREE_SUBJECT_EMPTY.NO_SUBJECTS_IN_CLASS;
    return FREE_SUBJECT_EMPTY.ALL_SUBJECTS_ASSIGNED;
}

const EMPTY_SUBJECT_COPY = {
    [FREE_SUBJECT_EMPTY.NO_SUBJECTS_IN_CLASS]: {
        Icon: MenuBookOutlinedIcon,
        title: "No subjects in this class yet",
        body: "Add subjects to this class first, then come back to assign teachers to each subject.",
        primaryLabel: "Add subjects",
    },
    [FREE_SUBJECT_EMPTY.ALL_SUBJECTS_ASSIGNED]: {
        Icon: SchoolOutlinedIcon,
        title: "All subjects are assigned",
        body: "Every subject in this class already has a teacher. Add more subjects to the class, or reassign from the teacher profile.",
        primaryLabel: "Add subjects",
    },
    [FREE_SUBJECT_EMPTY.NO_SUBJECT_SLOTS_FOR_TEACHER]: {
        Icon: SchoolOutlinedIcon,
        title: "No subjects available for this teacher",
        body: "Every subject in this class is already taught by another teacher. Add a new subject to the class, or remove an assignment from another teacher first.",
        primaryLabel: "Add subjects",
    },
};

const ChooseSubject = ({ situation }) => {
    const { currentUser } = useSelector((state) => state.user);
    const schoolId = getSchoolIdForUser(currentUser);
    const [periodLabel, setPeriodLabel] = useState("");

    useEffect(() => {
        if (!schoolId) return;
        fetchCurrentPeriod(schoolId)
            .then((p) => setPeriodLabel(p.label || ""))
            .catch(() => setPeriodLabel(""));
    }, [schoolId]);

    const params = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const staffRole = location.state?.staffRole;

    const [classID, setClassID] = useState("");
    const [teacherID, setTeacherID] = useState("");

    const { subjectsList, loading, error, response } = useSelector((state) => state.sclass);

    const resolvedClassId = useMemo(() => {
        if (situation === "Norm") return params.id || "";
        if (situation === "Teacher") return params.classID || "";
        return "";
    }, [situation, params.id, params.classID]);

    useEffect(() => {
        if (!resolvedClassId) return;
        setClassID(resolvedClassId);
        if (situation === "Teacher") {
            setTeacherID(params.teacherID || "");
        }
        if (situation === "Teacher" && params.teacherID) {
            dispatch(getTeacherFreeClassSubjects(resolvedClassId, params.teacherID));
        } else {
            dispatch(getTeacherFreeClassSubjects(resolvedClassId));
        }
    }, [situation, dispatch, resolvedClassId, params.teacherID]);

    const subjects = useMemo(
        () => (Array.isArray(subjectsList) ? dedupeSubjectsById(subjectsList) : []),
        [subjectsList],
    );

    const afterAssignNavigate = () => {
        if (situation === "Teacher" && teacherID) {
            navigate(`/Admin/teachers/teacher/${teacherID}`);
        } else {
            navigate("/Admin/teachers");
        }
    };

    const assignSubjectHandler = async (tid, subjectId) => {
        const result = await dispatch(updateTeachSubject(tid, subjectId));
        if (result?.success) {
            if (situation === "Teacher") {
                dispatch(getTeacherFreeClassSubjects(resolvedClassId, tid));
            } else {
                afterAssignNavigate();
            }
        }
    };

    const unassignSubjectHandler = async (tid, subjectId) => {
        const result = await dispatch(removeTeachSubject(tid, subjectId));
        if (result?.success) {
            dispatch(getTeacherFreeClassSubjects(resolvedClassId, tid));
        }
    };

    /* --- Loading --- */
    if (loading) {
        return (
            <Box
                sx={{
                    minHeight: "100%",
                    py: { xs: 2, sm: 3 },
                    background: (t) =>
                        t.palette.mode === "light"
                            ? `linear-gradient(180deg, ${alpha(t.palette.primary.main, 0.06)} 0%, ${t.palette.grey[50]} 28%)`
                            : alpha(t.palette.common.black, 0.12),
                }}
            >
                <Container maxWidth="md">
                    <Paper sx={{ borderRadius: 3, overflow: "hidden" }}>
                        <Box sx={{ px: 3, py: 2.5, bgcolor: (t) => alpha(t.palette.primary.main, 0.08) }}>
                            <Skeleton width="45%" height={36} />
                            <Skeleton width="70%" sx={{ mt: 1 }} />
                        </Box>
                        <Box sx={{ p: 2 }}>
                            {[1, 2, 3, 4, 5].map((i) => (
                                <Skeleton key={i} variant="rounded" height={52} sx={{ mb: 1, borderRadius: 1 }} />
                            ))}
                        </Box>
                    </Paper>
                </Container>
            </Box>
        );
    }

    /* --- API empty: distinguish no subjects vs all assigned --- */
    if (response) {
        const reason = normalizeEmptySubjectReason(response);
        const copy = EMPTY_SUBJECT_COPY[reason];
        const EmptyIcon = copy.Icon;
        return (
            <Box
                sx={{
                    minHeight: "100%",
                    py: { xs: 2, sm: 3 },
                    background: (t) =>
                        t.palette.mode === "light"
                            ? `linear-gradient(180deg, ${alpha(t.palette.primary.main, 0.06)} 0%, ${t.palette.grey[50]} 28%)`
                            : alpha(t.palette.common.black, 0.12),
                }}
            >
                <Container maxWidth="sm">
                    <Paper
                        elevation={0}
                        sx={{
                            borderRadius: 3,
                            p: { xs: 3, sm: 4 },
                            textAlign: "center",
                            border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
                        }}
                    >
                        <EmptyIcon sx={{ fontSize: 56, color: "primary.main", opacity: 0.9, mb: 1 }} />
                        <Typography variant="h5" fontWeight={800} gutterBottom>
                            {copy.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: "auto" }}>
                            {copy.body}
                        </Typography>
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="center">
                            <Button
                                variant="outlined"
                                startIcon={<ArrowBackIcon />}
                                onClick={() => navigate(-1)}
                                sx={{ borderRadius: 2 }}
                            >
                                Back
                            </Button>
                            {reason === FREE_SUBJECT_EMPTY.NO_SUBJECTS_IN_CLASS ||
                            reason === FREE_SUBJECT_EMPTY.ALL_SUBJECTS_ASSIGNED ? (
                                <Button
                                    variant="contained"
                                    startIcon={<PostAddIcon />}
                                    onClick={() => navigate("/Admin/addsubject/" + classID)}
                                    sx={{ borderRadius: 2, fontWeight: 700 }}
                                >
                                    {copy.primaryLabel}
                                </Button>
                            ) : (
                                <Button
                                    variant="contained"
                                    startIcon={<ArrowBackIcon />}
                                    onClick={afterAssignNavigate}
                                    sx={{ borderRadius: 2, fontWeight: 700 }}
                                >
                                    Back to teacher
                                </Button>
                            )}
                        </Stack>
                    </Paper>
                </Container>
            </Box>
        );
    }

    /* --- Error --- */
    if (error) {
        return (
            <Box sx={{ py: 4, px: 2 }}>
                <Container maxWidth="sm">
                    <Alert severity="error" sx={{ borderRadius: 2 }}>
                        {error?.message || "Could not load subjects for this class."}
                    </Alert>
                    <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ mt: 2 }}>
                        Back
                    </Button>
                </Container>
            </Box>
        );
    }

    /* --- Main list --- */
    return (
        <Box
            sx={{
                minHeight: "100%",
                py: { xs: 2, sm: 3 },
                background: (t) =>
                    t.palette.mode === "light"
                        ? `linear-gradient(180deg, ${alpha(t.palette.primary.main, 0.06)} 0%, ${t.palette.grey[50]} 28%)`
                        : alpha(t.palette.common.black, 0.12),
            }}
        >
            <Container maxWidth="md">
                <Paper
                    elevation={0}
                    sx={{
                        borderRadius: 3,
                        overflow: "hidden",
                        border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
                        boxShadow: (t) => `0 8px 32px ${alpha(t.palette.common.black, 0.06)}`,
                    }}
                >
                    <Box
                        sx={{
                            px: { xs: 2, sm: 3 },
                            py: 2.5,
                            background: (t) =>
                                `linear-gradient(115deg, ${t.palette.primary.dark} 0%, ${t.palette.primary.main} 52%, ${alpha(
                                    t.palette.info.main,
                                    0.85
                                )} 100%)`,
                            color: "common.white",
                        }}
                    >
                        <Stack direction="row" alignItems="flex-start" spacing={2}>
                            <Box
                                sx={{
                                    width: 52,
                                    height: 52,
                                    borderRadius: 2,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    bgcolor: alpha("#fff", 0.15),
                                }}
                            >
                                <MenuBookOutlinedIcon sx={{ fontSize: 28 }} />
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="overline" sx={{ opacity: 0.92, letterSpacing: 1 }}>
                                    {situation === "Teacher" ? "Reassign teaching load" : "New teacher assignment"}
                                </Typography>
                                <Typography variant="h5" fontWeight={800} sx={{ lineHeight: 1.2 }}>
                                    Choose a subject
                                </Typography>
                                <Typography variant="body2" sx={{ opacity: 0.92, mt: 0.5 }}>
                                    Assign one or more subjects for the current term. Each subject slot can only have one teacher per term.
                                </Typography>
                                {periodLabel ? (
                                    <Chip
                                        size="small"
                                        label={`Current term: ${periodLabel}`}
                                        sx={{ mt: 1, bgcolor: alpha("#fff", 0.16), color: "common.white", fontWeight: 700 }}
                                    />
                                ) : null}
                            </Box>
                        </Stack>
                    </Box>

                    <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 2, borderBottom: (t) => `1px solid ${t.palette.divider}` }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
                            <Button
                                size="small"
                                startIcon={<ArrowBackIcon />}
                                onClick={() => navigate(-1)}
                                sx={{ color: "text.secondary" }}
                            >
                                Back
                            </Button>
                            <Chip
                                size="small"
                                label={`${subjects.length} open ${subjects.length === 1 ? "slot" : "slots"}`}
                                color="primary"
                                variant="outlined"
                                sx={{ fontWeight: 600 }}
                            />
                        </Stack>
                    </Box>

                    {situation === "Teacher" && (
                        <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 1.5, borderBottom: (t) => `1px solid ${t.palette.divider}` }}>
                            <Stack direction="row" justifyContent="flex-end">
                                <Button
                                    variant="contained"
                                    onClick={afterAssignNavigate}
                                    sx={{ borderRadius: 2, fontWeight: 700 }}
                                >
                                    Done assigning subjects
                                </Button>
                            </Stack>
                        </Box>
                    )}

                    {subjects.length === 0 ? (
                        <Box sx={{ py: 6, px: 3, textAlign: "center" }}>
                            <Typography variant="h6" fontWeight={700} gutterBottom>
                                No open subject slots
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 440, mx: "auto" }}>
                                There are no unassigned subjects to pick right now. Add subjects to the class, or free a slot from
                                another teacher&apos;s assignment.
                            </Typography>
                            <Button
                                variant="contained"
                                startIcon={<PostAddIcon />}
                                onClick={() => navigate("/Admin/addsubject/" + classID)}
                                sx={{ borderRadius: 2 }}
                            >
                                Add subjects
                            </Button>
                        </Box>
                    ) : (
                        <TableContainer>
                            <Table size="medium" aria-label="Choose subject for teacher">
                                <TableHead>
                                    <TableRow
                                        sx={{
                                            bgcolor: (t) => alpha(t.palette.primary.main, 0.06),
                                            "& th": {
                                                fontWeight: 700,
                                                fontSize: 12,
                                                letterSpacing: 0.5,
                                                textTransform: "uppercase",
                                                color: "text.secondary",
                                                borderBottom: (t) => `1px solid ${t.palette.divider}`,
                                            },
                                        }}
                                    >
                                        <TableCell width={56}>#</TableCell>
                                        <TableCell>Subject</TableCell>
                                        <TableCell>Teacher</TableCell>
                                        <TableCell>Type</TableCell>
                                        <TableCell align="right">Action</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {subjects.map((subject, index) => (
                                        <TableRow
                                            key={subject._id}
                                            hover
                                            sx={{
                                                "&:last-child td": { border: 0 },
                                                "&:hover": {
                                                    boxShadow: (t) => `inset 3px 0 0 ${t.palette.primary.main}`,
                                                },
                                            }}
                                        >
                                            <TableCell sx={{ color: "text.secondary", fontWeight: 600 }}>{index + 1}</TableCell>
                                            <TableCell>
                                                <Typography fontWeight={700}>{subject.subName}</Typography>
                                                {subject.sessions ? (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {subject.sessions} period{subject.sessions === "1" ? "" : "s"} / week
                                                    </Typography>
                                                ) : null}
                                            </TableCell>
                                            <TableCell>
                                                {subject.assignedToThisTeacher ? (
                                                    <Chip size="small" label="You" color="success" variant="outlined" />
                                                ) : getSubjectTeacherLabel(subject) === "Unassigned" ? (
                                                    <Typography variant="caption" color="text.secondary">
                                                        Unassigned
                                                    </Typography>
                                                ) : (
                                                    <Chip
                                                        size="small"
                                                        label={getSubjectTeacherLabel(subject)}
                                                        color="default"
                                                        variant="outlined"
                                                    />
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    size="small"
                                                    label={subject.isElective ? "Elective" : "Compulsory"}
                                                    color={subject.isElective ? "info" : "success"}
                                                    variant="outlined"
                                                    sx={{ fontWeight: 600, borderRadius: 2 }}
                                                />
                                            </TableCell>
                                            <TableCell align="right">
                                                {situation === "Norm" ? (
                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        startIcon={<PersonAddAlt1Icon />}
                                                        onClick={() =>
                                                            navigate(`/Admin/staff/add/${subject._id}`, {
                                                                state: { staffRole: staffRole || "Teacher" },
                                                            })
                                                        }
                                                        sx={{ borderRadius: 2, fontWeight: 700 }}
                                                    >
                                                        Add staff
                                                    </Button>
                                                ) : subject.assignedToThisTeacher ? (
                                                    <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                                                        <Chip size="small" label="Assigned" color="success" variant="outlined" />
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            color="error"
                                                            onClick={() => unassignSubjectHandler(teacherID, subject._id)}
                                                            sx={{ borderRadius: 2, fontWeight: 700 }}
                                                        >
                                                            Remove
                                                        </Button>
                                                    </Stack>
                                                ) : (
                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        color="secondary"
                                                        onClick={() => assignSubjectHandler(teacherID, subject._id)}
                                                        sx={{ borderRadius: 2, fontWeight: 700, minWidth: 188 }}
                                                    >
                                                        Add subject
                                                    </Button>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Paper>
            </Container>
        </Box>
    );
};

export default ChooseSubject;
